import { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Global animation manager that prevents animations during navigation
 * and ensures proper scroll behavior across all pages
 */
export const useNavigationAwareAnimations = () => {
  const { pathname } = useLocation();
  const [visibleElements, setVisibleElements] = useState(new Set());
  const [isNavigating, setIsNavigating] = useState(false);
  const observerRef = useRef(null);
  const navigationTimeoutRef = useRef(null);

  // Handle navigation - reset animation state
  useEffect(() => {
    // Set navigating state and clear previous animations
    setIsNavigating(true);
    setVisibleElements(new Set());

    // Clear any existing timeout
    if (navigationTimeoutRef.current) {
      clearTimeout(navigationTimeoutRef.current);
    }

    // Re-enable animations after DOM is settled and scroll is complete
    navigationTimeoutRef.current = setTimeout(() => {
      setIsNavigating(false);
    }, 50);

    return () => {
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }
    };
  }, [pathname]);

  const observeElement = useCallback((element, id) => {
    if (!element || visibleElements.has(id) || isNavigating) return;

    if (!observerRef.current) {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !isNavigating) {
              setVisibleElements(prev => new Set([...prev, entry.target.dataset.animateId]));
            }
          });
        },
        {
          threshold: 0.1,
          rootMargin: '50px',
          // Add root margin to trigger animations slightly before elements are visible
        }
      );
    }

    element.dataset.animateId = id;
    observerRef.current.observe(element);
  }, [visibleElements, isNavigating]);

  // Cleanup observer on unmount
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }
    };
  }, []);

  return {
    observeElement,
    visibleElements,
    isNavigating,
    // Helper function to check if an element should be visible
    isVisible: (id) => visibleElements.has(id) && !isNavigating
  };
};

export default useNavigationAwareAnimations;
